import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsClientDetailInfoLoaded,
  IsClientDetailInfoLoading,
  loadClientDetails,
  SelectActualClientExternalData,
  SelectClientDetails,
  SelectNeedClientInfoUpdate,
  setIsClientDetailInfoLoading,
} from "../redux/reducers/clientDetails.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { useNavigate } from "react-router";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  SelectNeedHeaderRefresh,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";
import {
  IsLoadedWorkInfo,
  IsLoadingWorkInfo,
  loadWorkInfoTab,
  SelectInfoSavedClientId,
  SelectOrderedBy,
  SelectProjectsOrderedBy,
  SelectWorkOrdersOrderedBy,
  setIsLoadingWorkInfo,
} from "../redux/reducers/clientWork.reducer";

import { useClientsList } from "./useClientsList";

const loadHeaderInfoIfNeeded = async (
  inFlightRef,
  doneRef,
  needHeaderRefresh,
  clientId,
  clientForHeaderInfo,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.header ||
    (!needHeaderRefresh && doneRef.current.header === clientId) ||
    (!needHeaderRefresh &&
      clientForHeaderInfo &&
      clientForHeaderInfo.id === clientId)
  ) {
    return;
  }
  inFlightRef.current.header = true;
  dispatch(setIsActualClientForHeaderInfoLoading(true));
  const headerInfo = await ClientsService.getClientModuleHeaderInfo(clientId)
    .then((response) => {
      if (!response.success) {
        dispatch(
          addToast({
            mode: "error",
            message: response.message,
          })
        );
        dispatch(setIsActualClientForHeaderInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return response.data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting the client header information.",
        })
      );
      dispatch(setIsActualClientForHeaderInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.header = false;
  doneRef.current.header = clientId;
  dispatch(setActualClientForHeaderInfo(headerInfo));
};

const loadWorkInfoIfNeeded = async (
  inFlightRef,
  doneRef,
  clientId,
  isLoadedWorkInfo,
  infoSavedClientId,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.work ||
    doneRef.current.work === clientId ||
    (isLoadedWorkInfo && infoSavedClientId && infoSavedClientId === clientId)
  ) {
    return;
  }
  inFlightRef.current.work = true;
  dispatch(setIsLoadingWorkInfo(true));
  const clientsWorkInfo = await ClientsService.getClientWorkInfo(clientId)
    .then((data) => {
      if (!data.success) {
        dispatch(
          addToast({
            mode: "error",
            message: "Client not found",
          })
        );
        dispatch(setIsLoadingWorkInfo(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting the client work tab information.",
        })
      );
      dispatch(setIsLoadingWorkInfo(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.work = false;
  doneRef.current.work = clientId;
  dispatch(
    loadWorkInfoTab({
      clientId: clientId,
      data: clientsWorkInfo.data,
    })
  );
};

const loadClientDetailsIfNeeded = async (
  inFlightRef,
  doneRef,
  needClientInfoUpdate,
  clientId,
  isClientDetailInfoLoaded,
  clientDetails,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.details ||
    (!needClientInfoUpdate && doneRef.current.details === clientId) ||
    (!needClientInfoUpdate &&
      isClientDetailInfoLoaded &&
      clientDetails &&
      clientDetails.id === clientId)
  ) {
    return;
  }
  inFlightRef.current.details = true;
  dispatch(setIsClientDetailInfoLoading(true));
  const clientsInfo = await ClientsService.getClientInfo(clientId)
    .then((data) => {
      if (!data.success) {
        dispatch(
          addToast({
            mode: "error",
            message: "Client not found",
          })
        );
        dispatch(setIsClientDetailInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message: "Client not found",
        })
      );
      dispatch(setIsClientDetailInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.details = false;
  doneRef.current.details = clientId;
  if (clientsInfo) dispatch(loadClientDetails(clientsInfo));
};

export const useClientDetails = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded, activeUserId } =
    useSelector(selectUserRoles);
  const isClientDetailInfoLoading = useSelector(IsClientDetailInfoLoading);
  const isClientDetailInfoLoaded = useSelector(IsClientDetailInfoLoaded);
  const actualClientExternalData = useSelector(SelectActualClientExternalData);
  const clientDetails = useSelector(SelectClientDetails);
  const needClientInfoUpdate = useSelector(SelectNeedClientInfoUpdate);

  const infoSavedClientId = useSelector(SelectInfoSavedClientId);
  const isLoadingWorkInfo = useSelector(IsLoadingWorkInfo);
  const isLoadedWorkInfo = useSelector(IsLoadedWorkInfo);
  const { clientRecurringBillingsInfo: clientRecurringBillings } =
    useSelector(SelectOrderedBy);
  const { clientWorkOrdersInfo: clientWorkOrders } = useSelector(
    SelectWorkOrdersOrderedBy
  );
  const { clientProjectsInfo: clientProjects } = useSelector(
    SelectProjectsOrderedBy
  );

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  const needHeaderRefresh = useSelector(SelectNeedHeaderRefresh);
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  // Tracks which fetch blocks are in flight, and (in doneRef) which clientId
  // each block last completed for. The callback is async and not awaited by
  // the effect, so a block's dispatch can change a dependency and re-run the
  // effect while an earlier request is still pending. A second, overlapping
  // invocation is a *different* closure — its own `ClientForHeaderInfo`/
  // `clientDetails` reads are frozen at whatever they were when THAT closure
  // was created, so an in-flight-only guard isn't enough: a stale invocation
  // can still see them as not-yet-loaded right after a newer invocation has
  // already finished, and re-fire. doneRef is a plain mutable ref, so every
  // invocation (stale or not) reads its truly-current value.
  const inFlightRef = useRef<Record<string, boolean>>({});
  const doneRef = useRef<Record<string, number>>({});

  const loadClientInfo = useCallback(
    async (clientId) => {
      if (!rolesLoaded || !isClientsLoaded) {
        return;
      }
      // This check should go in all clients custom hooks
      if (!clientsIds.includes(clientId)) {
        dispatch(
          addToast({
            mode: "error",
            message: "You're not allowed to see this Client Page",
          })
        );
        navigate(ROUTE_FORBIDDEN_PAGE);
        return true;
      }
      // Load info header when user put a new URL
      await loadHeaderInfoIfNeeded(
        inFlightRef,
        doneRef,
        needHeaderRefresh,
        clientId,
        ClientForHeaderInfo,
        dispatch,
        navigate
      );
      // work info
      await loadWorkInfoIfNeeded(
        inFlightRef,
        doneRef,
        clientId,
        isLoadedWorkInfo,
        infoSavedClientId,
        dispatch,
        navigate
      );
      // page info
      await loadClientDetailsIfNeeded(
        inFlightRef,
        doneRef,
        needClientInfoUpdate,
        clientId,
        isClientDetailInfoLoaded,
        clientDetails,
        dispatch,
        navigate
      );
    },
    [
      dispatch,
      rolesLoaded,
      isClientsLoaded,
      needHeaderRefresh,
      needClientInfoUpdate,
    ]
  );

  useEffect(() => {
    loadClientInfo(clientId);
  }, [loadClientInfo]);

  return {
    ClientForHeaderInfo,
    activeUserId,
    isClientDetailInfoLoading,
    actualClientExternalData,
    clientDetails,
    userRolesList,
    rolesLoaded,
    isLoadingWorkInfo,
    clientRecurringBillings,
    clientWorkOrders,
    clientProjects,
  };
};
